import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Bars3Icon } from '@heroicons/react/24/outline';
import AdminNavModal from '../components/AdminNavModal';

export default function AdminDashboardNew() {
  const [stats, setStats] = useState({
    totalShipments: 0,
    pendingShipments: 0,
    deliveredShipments: 0,
    activeUsers: 0
  });
  const [recentShipments, setRecentShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        // Fetch recent shipments
        const shipmentsQuery = query(
          collection(db, 'shipments'),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const shipmentsSnapshot = await getDocs(shipmentsQuery);
        const shipments = shipmentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRecentShipments(shipments);

        // Fetch stats
        const statsQuery = query(collection(db, 'shipments'));
        const statsSnapshot = await getDocs(statsQuery);
        const allShipments = statsSnapshot.docs.map(doc => doc.data());
        
        setStats({
          totalShipments: allShipments.length,
          pendingShipments: allShipments.filter(s => s.status === 'pending').length,
          deliveredShipments: allShipments.filter(s => s.status === 'delivered').length,
          activeUsers: 0 // TODO: Implement user stats
        });

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    }

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Menu Button */}
      <button
        onClick={() => setIsNavOpen(true)}
        className="fixed top-4 right-4 z-40 p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-all"
      >
        <Bars3Icon className="w-6 h-6" />
      </button>

      {/* Navigation Modal */}
      <AdminNavModal isOpen={isNavOpen} onClose={() => setIsNavOpen(false)} />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="text-3xl mb-2">📦</div>
            <div className="text-3xl font-bold text-white mb-1">{stats.totalShipments}</div>
            <div className="text-gray-300 text-sm">Total Shipments</div>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="text-3xl mb-2">⏳</div>
            <div className="text-3xl font-bold text-white mb-1">{stats.pendingShipments}</div>
            <div className="text-gray-300 text-sm">Pending Shipments</div>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="text-3xl mb-2">✅</div>
            <div className="text-3xl font-bold text-white mb-1">{stats.deliveredShipments}</div>
            <div className="text-gray-300 text-sm">Delivered Shipments</div>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="text-3xl mb-2">👥</div>
            <div className="text-3xl font-bold text-white mb-1">{stats.activeUsers}</div>
            <div className="text-gray-300 text-sm">Active Users</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <button
            onClick={() => navigate('/admin/new')}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-colors text-left"
          >
            <div className="text-3xl mb-2">➕</div>
            <div className="text-xl font-bold text-white mb-1">New Shipment</div>
            <div className="text-gray-300 text-sm">Create a new shipment</div>
          </button>
          <button
            onClick={() => navigate('/admin/shipments')}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-colors text-left"
          >
            <div className="text-3xl mb-2">📋</div>
            <div className="text-xl font-bold text-white mb-1">Manage Shipments</div>
            <div className="text-gray-300 text-sm">View and manage all shipments</div>
          </button>
          <button
            onClick={() => navigate('/admin/settings')}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-colors text-left"
          >
            <div className="text-3xl mb-2">⚙️</div>
            <div className="text-xl font-bold text-white mb-1">Settings</div>
            <div className="text-gray-300 text-sm">Configure system settings</div>
          </button>
        </div>

        {/* Recent Shipments */}
        <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
          <h2 className="text-2xl font-bold text-white mb-6">Recent Shipments</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/20">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Tracking Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {recentShipments.map((shipment) => (
                  <tr key={shipment.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {shipment.trackingNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        shipment.status === 'delivered' 
                          ? 'bg-green-500/20 text-green-300'
                          : shipment.status === 'pending'
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {shipment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {new Date(shipment.createdAt?.toDate()).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <button
                        onClick={() => navigate(`/admin/edit/${shipment.id}`)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
} 